import type {
  ShopperAiTagId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  MESSAGE_CHANNELS,
  type MessageChannel,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketSort } from "@redotech/effect-api-shared/schema/query-options";
import {
  getSupportTicketSearchHighlightTerms,
  supportTicketCustomerSearchFields,
  supportTicketFilterData,
  SupportTicketFilterNames,
  type SupportTicketSearchHighlightTerm,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useRequiredContext } from "@redotech/react-util/context";
import ArrowDown from "@redotech/redo-icons/arrow-down.svg";
import ArrowUp from "@redotech/redo-icons/arrow-up.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { GetUser } from "@redotech/redo-model/user";
import type { AdvancedFilterData } from "@redotech/redo-model/views/views";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  type CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { deepCopyObject } from "@redotech/util/object";
import { useLiveQuery } from "@tanstack/react-db";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Fragment,
  memo,
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  type BadgeCellItem,
  BadgeOverflowCell,
} from "../../../return/returns-table-tags-cell";
import { pillThemeToBadgeColor } from "../../../return/tags/tag-colors";
import { StatusBadge } from "../../components/badges/support-ticket-badges";
import { PresenceAvatarGroup } from "../../components/presence-avatar-group";
import {
  useAddFilter,
  useAdvancedFilters,
  useQueryOptions,
  useServerColumnSort,
} from "../../filters/filters-context";
import { isSupportTicketUnread } from "../../hooks/use-support-ticket-card";
import { useSupportTicketPresence } from "../../hooks/use-support-ticket-data";
import { useGlobalSupportTicketCollections } from "../../providers/global-support-ticket-collections-context";
import {
  MessageChannelIcons,
  MessageChannelReadable,
} from "../../ui/thread-channel-ui";
import { EditableAssigneeCell } from "../components/editable-cells/editable-assignee-cell";
import { SupportTicketActionsMenu } from "../components/editable-cells/support-ticket-actions-menu";
import { useSupportTicketsTableContext } from "../support-tickets-table-context";
import type { SupportTicketColumnMeta } from "../types";
import {
  buildSupportTicketSearchHighlightFragmentFromText,
  buildSupportTicketSearchHighlightPreview,
  pickSupportTicketSearchHighlightMatch,
  type SupportTicketSearchHighlight,
  type SupportTicketSearchHighlightField,
} from "./support-ticket-search-highlights";
import * as styles from "./support-tickets-columns.module.css";

const ColumnHeader = memo(function ColumnHeader({ title }: { title: string }) {
  return (
    <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
      {title}
    </Text>
  );
});

const SortableColumnHeader = memo(function SortableColumnHeader({
  sortKind,
  title,
}: {
  sortKind: SupportTicketSort["kind"];
  title: string;
}) {
  const { sortDirection, toggleSort } = useServerColumnSort(sortKind);

  return (
    <Flex
      align="center"
      className={styles.clickableHeaderContent}
      cursor="pointer"
      gap="xs"
      onClick={toggleSort}
    >
      <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
        {title}
      </Text>
      {sortDirection !== null && (
        <Flex align="center" className="h-4 w-4">
          {sortDirection === "desc" ? <ArrowDown /> : <ArrowUp />}
        </Flex>
      )}
    </Flex>
  );
});

const FilterableColumnHeader = memo(function FilterableColumnHeader({
  title,
  filterName,
}: {
  title: string;
  filterName: SupportTicketFilterNames;
}) {
  const addFilter = useAddFilter();
  const { filters } = useAdvancedFilters();

  const handleClick = useCallback(() => {
    const alreadyExists = filters.some((f) => f.name === filterName);
    if (alreadyExists) {
      return;
    }
    const filter = deepCopyObject(
      supportTicketFilterData[filterName],
    ) as AdvancedFilterData;
    addFilter(filter);
  }, [addFilter, filters, filterName]);

  return (
    <Flex
      align="center"
      className={styles.clickableHeaderContent}
      cursor="pointer"
      gap="xs"
      onClick={handleClick}
    >
      <Text fontSize="xxs" fontWeight="medium" textColor="tertiary">
        {title}
      </Text>
    </Flex>
  );
});

function formatDateWithTime(date: Date): { date: string; time: string | null } {
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();
  if (!isCurrentYear) {
    const dateStr = date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return { date: dateStr, time: null };
  }
  const dateStr = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const timeStr = date
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
  return { date: dateStr, time: timeStr };
}

function getLastResponseAt(
  supportTicket: SupportTicketListItemResponse,
): Date | null {
  const lastResponseAt = Math.max(
    supportTicket.lastCustomerMessageAt?.getTime() ?? -Infinity,
    supportTicket.lastMerchantMessageAt?.getTime() ?? -Infinity,
  );

  return lastResponseAt === -Infinity ? null : new Date(lastResponseAt);
}

const ReadColumnHeader = memo(function ReadColumnHeader() {
  const { data, isSupportTicketSelected, setAllSupportTicketsSelected } =
    useSupportTicketsTableContext();

  const selectedVisibleSupportTicketCount = useMemo(
    () =>
      data.filter((conversation) => isSupportTicketSelected(conversation.id))
        .length,
    [data, isSupportTicketSelected],
  );

  const checkboxValue: CheckboxValue =
    selectedVisibleSupportTicketCount === 0
      ? false
      : selectedVisibleSupportTicketCount === data.length
        ? true
        : "indeterminate";

  const handleSetValue = useCallback(
    (value: CheckboxValue) => {
      setAllSupportTicketsSelected(value === true || value === "indeterminate");
    },
    [setAllSupportTicketsSelected],
  );

  return (
    <Flex align="center" justify="center" pl="sm">
      <RedoCheckbox
        disabled={data.length === 0}
        setValue={handleSetValue}
        size="xs"
        value={checkboxValue}
      />
    </Flex>
  );
});

const ReadColumnCell = memo(function ReadColumnCell({
  supportTicket,
  rowIndex,
}: {
  supportTicket: SupportTicketListItemResponse;
  rowIndex: number;
}) {
  const {
    hoveredSupportTicketId,
    hasAnySelectedSupportTickets,
    isSupportTicketSelected,
    setSupportTicketSelected,
  } = useSupportTicketsTableContext();
  const shiftKeyRef = useRef(false);

  const shouldShowCheckbox = useMemo(
    () =>
      hasAnySelectedSupportTickets ||
      hoveredSupportTicketId === supportTicket.id,
    [hasAnySelectedSupportTickets, hoveredSupportTicketId, supportTicket.id],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleInputClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    shiftKeyRef.current = event.shiftKey;
    event.stopPropagation();
  }, []);

  const handleSetValue = useCallback(
    (value: CheckboxValue) => {
      setSupportTicketSelected({
        supportTicketId: supportTicket.id,
        rowIndex,
        checked: value === true || value === "indeterminate",
        shiftKey: shiftKeyRef.current,
      });
      shiftKeyRef.current = false;
    },
    [setSupportTicketSelected, supportTicket.id, rowIndex],
  );

  if (shouldShowCheckbox) {
    return (
      <Flex
        align="center"
        className={styles.readCellContainer}
        justify="center"
        pl="sm"
      >
        <div onClick={handleClick} onMouseDown={handleClick}>
          <RedoCheckbox
            onClick={handleInputClick}
            setValue={handleSetValue}
            size="xs"
            value={isSupportTicketSelected(supportTicket.id)}
          />
        </div>
      </Flex>
    );
  }

  if (isSupportTicketUnread(supportTicket)) {
    return (
      <Flex
        align="center"
        className={styles.readCellContainer}
        justify="center"
      >
        <div className={styles.unreadIndicator} />
      </Flex>
    );
  }

  return null;
});

const TagsCell = memo(function TagsCell({
  tagIds,
}: {
  tagIds: readonly ShopperAiTagId[];
}) {
  const collections = useGlobalSupportTicketCollections();
  const { data: tags } = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [],
  );
  const currentTags = tags.filter((tag) => tagIds.includes(tag.id));

  const badges: BadgeCellItem[] = currentTags.map((tag) => ({
    text: tag.name,
    color: pillThemeToBadgeColor[tag.pillTheme ?? PillTheme.NORMAL],
  }));

  return <BadgeOverflowCell badges={badges} emptyText="" />;
});

const channelDisplayOrder = new Map<MessageChannel, number>(
  MESSAGE_CHANNELS.map((channel, index) => [channel, index]),
);

function getSortedUniqueChannels(
  channels: readonly MessageChannel[],
): MessageChannel[] {
  return [...new Set(channels)].sort(
    (a, b) =>
      (channelDisplayOrder.get(a) ?? Number.MAX_SAFE_INTEGER) -
      (channelDisplayOrder.get(b) ?? Number.MAX_SAFE_INTEGER),
  );
}

const ChannelsCell = memo(function ChannelsCell({
  channels,
}: {
  channels: readonly MessageChannel[];
}) {
  const badges: BadgeCellItem[] = useMemo(
    () =>
      getSortedUniqueChannels(channels).map((channel) => ({
        color: "gray",
        dataTestId: `support-ticket-channel-${channel}`,
        segmentLeading: { type: "icon", Icon: MessageChannelIcons[channel] },
        text: MessageChannelReadable[channel],
      })),
    [channels],
  );

  return <BadgeOverflowCell badges={badges} emptyText="" />;
});

const ActiveUsersCell = memo(function ActiveUsersCell({
  supportTicketId,
}: {
  supportTicketId: SupportTicketId;
}) {
  const { data: presenceUsers } = useSupportTicketPresence(supportTicketId);
  const team = useRequiredContext(TeamContext);
  const userMap = useMemo(
    () =>
      new Map(
        team.users.map((u) => {
          const user = u.user as GetUser;
          return [user._id, user.name];
        }),
      ),
    [team.users],
  );

  if (presenceUsers.length === 0) {
    return null;
  }

  return (
    <PresenceAvatarGroup
      maxAvatars={2}
      userMap={userMap}
      users={presenceUsers}
    />
  );
});

const SummaryCell = memo(function SummaryCell({
  summary,
  searchHighlights,
}: {
  summary: string | null;
  searchHighlights?: readonly SupportTicketSearchHighlight[];
}) {
  const activeHighlights = useActiveSearchHighlights();
  const summaryHighlightFields = useMemo(
    () =>
      SUMMARY_SEARCH_HIGHLIGHT_FIELDS.filter((field) =>
        activeHighlights.fields.has(field),
      ),
    [activeHighlights.fields],
  );
  const highlight =
    summaryHighlightFields.length > 0
      ? pickSupportTicketSearchHighlightMatch(
          searchHighlights,
          summaryHighlightFields,
        )
      : null;

  if (highlight) {
    const isSearchPreview = highlight.field !== "summary";
    return (
      <HighlightedSearchText
        block
        fragment={highlight.fragment}
        textColor={isSearchPreview ? "primary" : "tertiary"}
      />
    );
  }

  return (
    <Text
      fontSize="xs"
      fontStyle={summary ? undefined : "italic"}
      overflow="hidden"
      textColor="tertiary"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {summary ?? "No generated summary"}
    </Text>
  );
});

const SUMMARY_SEARCH_HIGHLIGHT_FIELDS: readonly SupportTicketSearchHighlightField[] =
  ["summary", "emailSubject", "messageContent"];

const CustomerCell = memo(function CustomerCell({
  supportTicket,
}: {
  supportTicket: SupportTicketListItemResponse;
}) {
  const activeHighlights = useActiveSearchHighlights();
  const customerHighlightFields = useMemo(
    () =>
      supportTicketCustomerSearchFields.filter((field) =>
        activeHighlights.fields.has(field),
      ),
    [activeHighlights.fields],
  );
  const customerHighlight =
    customerHighlightFields.length > 0
      ? pickSupportTicketSearchHighlightMatch(
          supportTicket.searchHighlights,
          customerHighlightFields,
        )
      : null;
  const fallbackCustomerFragment = useMemo(
    () =>
      customerHighlight
        ? null
        : buildCustomerSearchHighlightFragment(
            supportTicket,
            activeHighlights.terms,
          ),
    [activeHighlights.terms, customerHighlight, supportTicket],
  );

  const customerFragment =
    customerHighlight?.fragment ?? fallbackCustomerFragment;

  if (customerFragment) {
    return (
      <HighlightedSearchText
        block
        fragment={customerFragment}
        textColor="primary"
      />
    );
  }

  return (
    <Text
      fontSize="xs"
      fontWeight={isSupportTicketUnread(supportTicket) ? "semibold" : "regular"}
      textColor="primary"
    >
      {supportTicket.customer?.displayName ?? "Unknown Customer"}
    </Text>
  );
});

const HighlightedSearchText = memo(function HighlightedSearchText({
  block = false,
  fragment,
  textColor,
}: {
  block?: boolean;
  fragment: string;
  textColor: "primary" | "tertiary";
}) {
  const segments = useMemo(
    () => buildSupportTicketSearchHighlightPreview(fragment),
    [fragment],
  );

  return (
    <Text
      as="span"
      display={block ? "block" : undefined}
      fontSize="xs"
      fontWeight="regular"
      overflow="hidden"
      textColor={textColor}
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {segments.map((segment, index) => (
        <Fragment key={index}>
          {segment.highlighted ? (
            <span className={styles.searchHighlight}>{segment.text}</span>
          ) : (
            segment.text
          )}
        </Fragment>
      ))}
    </Text>
  );
});

interface ActiveSearchHighlights {
  fields: ReadonlySet<SupportTicketSearchHighlightField>;
  terms: readonly SupportTicketSearchHighlightTerm[];
}

const customerSearchHighlightFields =
  new Set<SupportTicketSearchHighlightField>(supportTicketCustomerSearchFields);

function useActiveSearchHighlights(): ActiveSearchHighlights {
  const { filterGroup } = useQueryOptions();
  return useMemo(() => {
    const terms = getSupportTicketSearchHighlightTerms(filterGroup);
    const fields = new Set(terms.flatMap((term) => term.fields));
    return { fields, terms };
  }, [filterGroup]);
}

function buildCustomerSearchHighlightFragment(
  supportTicket: SupportTicketListItemResponse,
  terms: readonly SupportTicketSearchHighlightTerm[],
): string | null {
  const customer = supportTicket.customer;
  if (!customer) {
    return null;
  }

  const customerSearchTerms = terms
    .filter((term) =>
      term.fields.some((field) => customerSearchHighlightFields.has(field)),
    )
    .map((term) => term.searchTerm);
  const customerTexts = [
    customer.displayName,
    customer.email,
    customer.phone,
  ].flatMap((text) => (text ? [text] : []));

  for (const text of customerTexts) {
    const fragment = buildSupportTicketSearchHighlightFragmentFromText(
      text,
      customerSearchTerms,
    );
    if (fragment) {
      return fragment;
    }
  }

  return null;
}

/**
 * Get column definitions for the support tickets table
 * Column order:
 * 1. Read indicator (dot if unread)
 * 2. Ticket #
 * 3. Status
 * 4. Customer
 * 5. Active users (presence avatars)
 * 6. Summary
 * 7. Priority
 * 8. Channel
 * 9. Assignee
 * 10. Last response
 * 11. Created at
 * 12. Actions (3-dot menu)
 */
export function getSupportTicketsTableColumns(_options?: {
  enablePinning?: boolean;
}): ColumnDef<SupportTicketListItemResponse, unknown>[] {
  return [
    {
      id: "read",
      accessorKey: "readAt",
      header: () => <ReadColumnHeader />,
      cell: ({ row }) => (
        <ReadColumnCell rowIndex={row.index} supportTicket={row.original} />
      ),
      meta: {
        label: "Read",
        variant: "text",
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      size: 24,
      minSize: 24,
    },
    {
      id: "ticketNumber",
      accessorKey: "ticketNumber",
      header: () => <ColumnHeader title="Ticket #" />,
      cell: ({ row }) => {
        const ticketNumber = row.original.ticketNumber;
        const ticketDisplay = ticketNumber != null ? `#${ticketNumber}` : "";
        return (
          <Text
            fontSize="xs"
            fontWeight={
              isSupportTicketUnread(row.original) ? "semibold" : "regular"
            }
            textColor="primary"
          >
            {ticketDisplay}
          </Text>
        );
      },
      meta: {
        label: "Ticket #",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 80,
    },
    {
      id: "status",
      accessorKey: "status",
      header: () => <ColumnHeader title="Status" />,
      cell: ({ row }) => {
        return <StatusBadge size="sm" status={row.original.status} />;
      },
      meta: {
        label: "Status",
        variant: "badge",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: true,
      filterFn: (row, id, value: string[]) => {
        const cellValue = row.getValue(id);
        return value.includes(String(cellValue));
      },
      size: 100,
      minSize: 100,
    },
    {
      id: "customer",
      accessorKey: "mongoCustomerId",
      header: () => <ColumnHeader title="Customer" />,
      cell: ({ row }) => <CustomerCell supportTicket={row.original} />,
      meta: {
        label: "Customer",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 160,
    },
    {
      id: "activeUsers",
      header: () => null,
      cell: ({ row }) => <ActiveUsersCell supportTicketId={row.original.id} />,
      meta: {
        label: "Active users",
        variant: "text",
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      size: 75,
      minSize: 75,
    },
    {
      id: "summary",
      accessorKey: "summary",
      header: () => <ColumnHeader title="Summary" />,
      cell: ({ row }) => (
        <SummaryCell
          searchHighlights={row.original.searchHighlights}
          summary={row.original.summary}
        />
      ),
      meta: {
        label: "Summary",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 200,
    },
    {
      id: "platform",
      accessorKey: "channels",
      header: () => (
        <FilterableColumnHeader
          filterName={SupportTicketFilterNames.CHANNELS}
          title="Channel"
        />
      ),
      cell: ({ row }) => {
        const channels = row.original.channels;
        if (channels.length === 0) {
          return null;
        }
        return <ChannelsCell channels={channels} />;
      },
      meta: {
        label: "Channel",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 200,
      minSize: 120,
    },
    {
      id: "priority",
      accessorKey: "priority",
      header: () => <ColumnHeader title="Priority" />,
      cell: ({ row }) => {
        const priority = row.original.priority;
        if (priority !== "URGENT") {
          return null;
        }
        return <RedoBadge color="error" size="sm" text="Urgent" />;
      },
      meta: {
        label: "Priority",
        variant: "badge",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, id, value: string[]) => {
        const cellValue = row.getValue(id);
        if (!cellValue) {
          return false;
        }
        return value.includes(String(cellValue));
      },
      size: 80,
      minSize: 80,
    },
    {
      id: "assignee",
      header: () => (
        <FilterableColumnHeader
          filterName={SupportTicketFilterNames.ASSIGNEES}
          title="Assignee"
        />
      ),
      cell: ({ row }) => {
        return <EditableAssigneeCell supportTicket={row.original} />;
      },
      meta: {
        label: "Assignee",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 80,
      minSize: 80,
    },
    {
      id: "lastResponse",
      accessorFn: getLastResponseAt,
      header: () => (
        <SortableColumnHeader sortKind="lastResponseAt" title="Last response" />
      ),
      cell: ({ getValue }) => {
        const date = getValue<Date | null>();
        if (!date) {
          return null;
        }
        const formatted = formatDateWithTime(date);
        return (
          <Flex align="center" gap="xs">
            <Text fontSize="xs" fontWeight="regular" textColor="primary">
              {formatted.date}
            </Text>
            {formatted.time && (
              <Text fontSize="xs" textColor="tertiary">
                {formatted.time}
              </Text>
            )}
          </Flex>
        );
      },
      meta: {
        label: "Last response",
        variant: "date",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: true,
      enableColumnFilter: false,
      size: 130,
    },
    {
      id: "tags",
      header: () => (
        <FilterableColumnHeader
          filterName={SupportTicketFilterNames.SUPPORT_TICKET_TAGS}
          title="Tags"
        />
      ),
      cell: ({ row }) => <TagsCell tagIds={row.original.tagIds} />,
      meta: {
        label: "Tags",
        variant: "text",
        headerClassName: styles.headerCellHover,
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      size: 200,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <SupportTicketActionsMenu supportTicket={row.original} />
      ),
      meta: {
        label: "Actions",
        variant: "text",
      } satisfies SupportTicketColumnMeta,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      size: 48,
      minSize: 48,
    },
  ];
}
